import camelizeKeys from 'lib/camelizeKeys'

import reduxStore from 'lib/reduxStore'
import {
  ejectRequestInterceptor,
  ejectResponseInterceptor,
  setRequestInterceptors,
  setResponseInterceptors
} from 'lib/api/restClient'
import { allRoutesConfig, gotoLoginAppLogout } from 'lib/routesConfig'
import { reauthenticate, reauthenticateLoginApp } from 'lib/reauthenticate'
import { clearAllBccStorage } from 'lib/oauth2/bccOAuth2Utils'
import { RootState } from 'redux/toolkit/store'
import appConfig, { AppNames } from 'config/appConfig'
import { isLoginAppEnabled, isOnOAuth2Page } from 'lib/loginAppUtils'
import logger from '../logger'

export async function responseDataInterceptor(data: any) {
  if (data.config.headers.isToolkitCompatible) {
    return {
      ...data,
      data: {
        ...camelizeKeys(data.data)
      }
    }
  }

  return data
}

/* eslint-disable no-param-reassign */
async function requestInterceptor(requestConfig: any) {
  const authStore = reduxStore.getState().auth
  const accessToken = authStore?.accessToken

  if (accessToken?.length) {
    requestConfig.headers = {
      // add authentication header
      Authorization: `Bearer ${accessToken}`,
      ...(requestConfig.headers || {})
    }
  }

  return requestConfig
}

/* eslint-enable no-param-reassign */

// error handles
const REDIRECT_STATUS_CODES = [
  401 // Unauthorized
  // TODO: add back 403 check after this status code is used correctly on api-gateway side
  // 403 // Forbidden
  // Note: 493 is NOT included here because wizard flow uses 493 for WizardServiceAccessTokenExpiredError
  // which needs special handling (redirect to SETUP_FLOW_IPIR_AUTHORIZE). F10 handles 493 inline below.
]

const LOGIN_ENTRY_PATHS = [
  allRoutesConfig.LOGIN?.path,
  allRoutesConfig.BCC_LOGIN?.path,
  allRoutesConfig.CUDASPT_LOGIN?.path
].filter(urlPath => !!urlPath)

const REDIRECT_URLS_BLOCKLIST: string[] = LOGIN_ENTRY_PATHS
// Skip interceptor for auth endpoints and logout to avoid redirect loops
const SKIP_API_ENDPOINTS: string[] = [
  '/verify',
  '/login',
  '/bcc-login',
  '/auth0-login',
  '/auth-service/logout',
  '/user/session', // Logout API - don't intercept errors during logout
  '/internal/logout' // Legacy logout path
]

/**
 * Skipped on the OAuth2 authorize/callback pages: they own their error handling, and restarting from
 * there could loop (authorize -> callback -> 401 -> authorize).
 */
function restartLoginAppAuthorization() {
  if (isOnOAuth2Page()) {
    logger.info('Auth error on OAuth2 page - leaving error handling to the page')
    return
  }
  logger.info('Auth error on Login App session - restarting Login App authorization flow')
  reauthenticateLoginApp()
}

export function responseErrorInterceptor(error: any) {
  const isAllowedApiEndpoint = !SKIP_API_ENDPOINTS.some((endpoint: string) => error?.config?.url?.includes(endpoint))
  const encodedReturnUrl = encodeURIComponent(window.location.href)
  const authStore = (reduxStore.getState() as RootState).auth
  const bccLoginUrl = `${process.env.REACT_APP_GATEWAY_URL}/internal/bcc-login?return_url=${encodedReturnUrl}&account_id=${authStore.accessTokenObject?.accountId}&user_id=${authStore.accessTokenObject?.userId}`

  // Handle 409 for Login App account switch - redirect to OAuth2 flow
  // This is a fallback if /switch-account is somehow called for a Login App session
  if (error?.status === 409 && error?.data?.detail?.error_code === 'SOAP_ACCOUNT_SWITCH_NOT_ALLOWED') {
    // Extract account ID from the original request and redirect to OAuth2 flow
    // Use React OAuth2 authorize page which handles auth process creation
    // Note: Axios serializes config.data to JSON string, so we need to parse it
    let accountId: string | undefined
    try {
      const requestData = typeof error?.config?.data === 'string' ? JSON.parse(error.config.data) : error?.config?.data
      accountId = requestData?.accountId
    } catch {
      // Ignore parse errors - accountId will be undefined
    }
    if (accountId) {
      logger.info('Login App session attempted SOAP account switch, redirecting to OAuth2 flow')
      window.location.href = `/admin/oauth2/authorize?connection=login_app&account_hint=${accountId}`
      return Promise.reject(error)
    }
  }

  // F11: Handle 409 for /bcc-login when Login App is enabled
  // APIGW returns this instead of SOAP auth - redirect to OAuth2 authorize flow
  // Scoped to bcc-login URL for defense in depth (error code is unique, but URL check adds safety)
  const isBccLoginRequest = error?.config?.url?.includes('/bcc-login')
  if (isBccLoginRequest && error?.status === 409 && error?.data?.detail?.error_code === 'LOGIN_APP_REDIRECT_REQUIRED') {
    const authorizeParams = error?.data?.detail?.authorize_params || {}

    // Only redirect if connection param is present (required for valid OAuth2 flow)
    if (authorizeParams.connection) {
      const searchParams = new URLSearchParams()
      searchParams.set('connection', authorizeParams.connection)
      if (authorizeParams.nav) searchParams.set('nav', authorizeParams.nav)
      if (authorizeParams.account_hint) searchParams.set('account_hint', authorizeParams.account_hint)
      if (authorizeParams.entry_path) searchParams.set('entry_path', authorizeParams.entry_path)

      logger.info('Login App enabled, redirecting to OAuth2 authorize flow')
      window.location.href = `/admin/oauth2/authorize?${searchParams.toString()}`
      // Return never-resolving promise to prevent any error handlers from running during navigation
      // eslint-disable-next-line @typescript-eslint/no-empty-function
      return new Promise(() => {})
    }
  }

  // F10: Check if this is a Login App session (only for admin/wizard, not enduser/cudaspt)
  // Fallback to loginAppEnabled ONLY when accessTokenObject is absent (early auth error before token loaded).
  // If accessTokenObject exists but authMode is missing, that's a SOAP session - don't use fallback.
  // Uses cookie override (login_app_override) for QA testing.
  const { accessTokenObject } = authStore
  const authMode = accessTokenObject?.authMode
  const serverLoginAppEnabled = !!(reduxStore.getState() as RootState).app.publicAppSettings?.loginAppEnabled
  const loginAppEnabled = isLoginAppEnabled(serverLoginAppEnabled)
  const isAdminOrWizardApp = appConfig.APP.NAME === AppNames.admin || appConfig.APP.NAME === AppNames.wizard
  const isLoginAppSession = isAdminOrWizardApp && (authMode === 'login_app' || (!accessTokenObject && loginAppEnabled))

  // F10: Handle 493 JWT/Session account mismatch for Login App sessions
  // Note: Only handle JWT_SESSION_ACCOUNT_MISMATCH here. Other 493 errors (e.g., WizardServiceAccessTokenExpiredError)
  // are handled by their specific thunks and should NOT trigger logout.
  if (
    error?.status === 493 &&
    error?.data?.detail?.error_code === 'JWT_SESSION_ACCOUNT_MISMATCH' &&
    isLoginAppSession
  ) {
    logger.info('493 JWT/Session account mismatch, redirecting to Login App logout')
    clearAllBccStorage()
    gotoLoginAppLogout()
    return Promise.reject(error)
  }

  // logout the user if any requests failed with one of REDIRECT_STATUS_CODES and not whitelisted and blocked
  if (
    isAllowedApiEndpoint &&
    REDIRECT_STATUS_CODES.includes(error?.status) &&
    !REDIRECT_URLS_BLOCKLIST.includes(window.location.pathname)
  ) {
    if (error?.data?.detail === 'OutOfSyncCurrentAccount') {
      if (isLoginAppSession) {
        restartLoginAppAuthorization()
      } else {
        reauthenticate()
      }
    } else {
      logger.info('401 returned trying to grab session from bcc')
      if (isLoginAppSession) {
        restartLoginAppAuthorization()
      } else {
        window.location.href = bccLoginUrl
      }
    }
  }

  // access token is expired
  if (error?.status === 422 && error?.data?.detail === 'Signature has expired') {
    logger.info('status 422 and signature has expired returned. Trying to grab session from bcc')
    if (isLoginAppSession) {
      restartLoginAppAuthorization()
    } else {
      window.location.href = bccLoginUrl
    }
  }

  return Promise.reject(error)
}

export default function registerAxiosHooks() {
  ejectRequestInterceptor()
  setRequestInterceptors(requestInterceptor)

  ejectResponseInterceptor()
  setResponseInterceptors(responseDataInterceptor, responseErrorInterceptor)
}
